import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { BlueprintType } from '../../../../api/blueprints/blueprint.types';
import { IBlueprintsDAO } from '../../../../api/blueprints/blueprints.dao';
import { IOrderTemplateDetailsDTO } from '../../../../api/blueprints/order.template.types';
import {
    ISalesforceOrderDAO,
    ISalesforceOrderFormDataDTO,
} from '../../../../api/salesforce-order.dao';
import { alertServerError } from '../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { AbstractFormViewModelController } from '../../../../infrastructure/implementations/form-viewmodel-controller';
import { FormVmValueType } from '../../../../infrastructure/form-viewmodel-controller';
import { ILocalStorageResponse, ILocalStorageService } from '../../../../infrastructure/local-storage-service';
import { IGlobalStorage } from '../../../../infrastructure/global-storage';
import { IReactIntlProvider } from '../../../../infrastructure/react-intl-provider';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { BLUEPRINTS_TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import { ORDER_SUBTYPE_BY_TYPE } from '../../../_events/_manage-event/shared/salesforce-order/add-salesforce-order.fields';
import {
    applyBillingMethodChange,
    applyCancellationReasonChange,
    applyHighProfileOrderChange,
    applyHighProfileReasoningChange,
    applyOrderTypeChange,
    IOrderFieldValueStore,
} from '../../../_events/_manage-event/shared/salesforce-order/order-field-dependencies';
import { IOrderFormFieldsVM, IOrderFormSelectOption } from '../../../_events/_manage-event/shared/salesforce-order/order-form-fields.types';
import {
    createLookupFieldDispatch,
    getCachedOpportunitySearch,
    orderTemplateAccountSearchCacheKey,
    orderTemplateOpportunitySearchCacheKey,
    searchOpportunitiesWithCache,
    SF_ACCOUNT_SEARCH_CACHE_MINUTES,
    stringValue as readStringValue,
    toIdNameOptions,
} from '../../../_events/_manage-event/shared/salesforce-order/order-form-values';
import { STANDARD_ORDER_RECORD_TYPE } from '../../../_events/_manage-event/shared/salesforce-order/order-field-visibility';
import { ORDER_TEMPLATE_CONFIGURED_KEYS, ORDER_TEMPLATE_FORM_CONFIG } from './order-template-form.config';
import { ORDER_TEMPLATE_PRELOAD_ERROR_TEXT } from './order-template-form.texts';
import { toCreateOrderTemplateDto, toOrderTemplateTrackingEvent } from './order-template.mapper';

interface IAccountSearchCache {
    term: string;
    accounts: { id: string; name: string }[];
}

export class OrderTemplateFormVM extends AbstractFormViewModelController implements IOrderFormFieldsVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, OrderTemplateFormVM.messageFrom(e));
    }

    private static messageFrom(error: Error): string {
        return IoC.Container.get(IErrorParsing).extractMessage(error);
    }

    public readonly mode = 'create' as const;
    @mobx.observable
    public isSaving: boolean = false;
    @mobx.observable
    private formData: ISalesforceOrderFormDataDTO | null = null;
    @mobx.observable
    private corporateRelatedOption: IOrderFormSelectOption | null = null;
    @mobx.observable
    private billToLegalEntityOption: IOrderFormSelectOption | null = null;
    @mobx.observable
    private channelAgencyReferralOption: IOrderFormSelectOption | null = null;
    @mobx.observable
    private opportunityOption: IOrderFormSelectOption | null = null;
    @mobx.observable
    public preloadBlocked = false;
    private preloadErrorMessage = '';
    private searchRequestId: Record<string, number> = {};

    constructor(
        private readonly accountId: number,
        private readonly apiCallSuccessCallback: () => void,
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly salesforceOrderDAO: ISalesforceOrderDAO = IoC.Container.get(ISalesforceOrderDAO),
        @IoC.Inject private readonly localStorageService: ILocalStorageService = IoC.Container.get(ILocalStorageService),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
    ) {
        super({}, 'OrderTemplate', ORDER_TEMPLATE_FORM_CONFIG);
    }

    public get isCloneFieldsLocked(): boolean {
        return false;
    }

    public get isBillingLocked(): boolean {
        return false;
    }

    public getRecordType(): string {
        return STANDARD_ORDER_RECORD_TYPE;
    }

    public async loadFormData(): Promise<void> {
        try {
            const data = await this.salesforceOrderDAO.getOrderFormData({ accountId: this.accountId });
            const blocked = !data || data.available === false;
            const message = data?.message || this.intlProvider.getIntl().formatMessage(ORDER_TEMPLATE_PRELOAD_ERROR_TEXT);
            this.setPreloadState(blocked, blocked ? message : '');
            if (blocked) {
                alertServerError('Error', message);
            }
            if (data) {
                this.setFormData(data);
            }
        } catch (error) {
            const message = OrderTemplateFormVM.messageFrom(error);
            this.setPreloadState(true, message);
            OrderTemplateFormVM.handleApiException(error);
        }
    }

    public searchAccounts = async (fieldKey: string, term: string): Promise<IOrderFormSelectOption[]> => {
        const requestId = (this.searchRequestId[fieldKey] ?? 0) + 1;
        this.searchRequestId[fieldKey] = requestId;
        const cacheKey = orderTemplateAccountSearchCacheKey(this.accountId, fieldKey);
        const cached = this.localStorageService.getLocalStorageValue<ILocalStorageResponse>(cacheKey);
        if (cached && cached.value && this.localStorageService.checkTimeRange(cached.creationDate, cacheKey, SF_ACCOUNT_SEARCH_CACHE_MINUTES)) {
            const entry = cached.value as IAccountSearchCache;
            if (entry.term === term) {
                return entry.accounts.map(account => ({ value: account.id, label: account.name }));
            }
        }
        const accounts = await this.salesforceOrderDAO.searchAccounts({ accountId: this.accountId, searchTerm: term });
        const options = accounts.map(account => ({ value: account.id, label: account.name }));
        if (this.searchRequestId[fieldKey] !== requestId) {
            return options;
        }
        this.localStorageService.setLocalStorageValue({
            name: cacheKey,
            value: { term: term, accounts: accounts },
            creationDate: Date.now(),
        });
        return options;
    };

    public getCachedAccounts = (fieldKey: string): { term: string; options: IOrderFormSelectOption[] } | null => {
        const cacheKey = orderTemplateAccountSearchCacheKey(this.accountId, fieldKey);
        const cached = this.localStorageService.getLocalStorageValue<ILocalStorageResponse>(cacheKey);
        if (cached && cached.value && this.localStorageService.checkTimeRange(cached.creationDate, cacheKey, SF_ACCOUNT_SEARCH_CACHE_MINUTES)) {
            const entry = cached.value as IAccountSearchCache;
            return { term: entry.term, options: entry.accounts.map(account => ({ value: account.id, label: account.name })) };
        }
        return null;
    };

    public searchOpportunities = async (term: string): Promise<IOrderFormSelectOption[]> => {
        const requestId = (this.searchRequestId.opportunity ?? 0) + 1;
        this.searchRequestId.opportunity = requestId;
        const cacheKey = orderTemplateOpportunitySearchCacheKey(this.accountId);
        return searchOpportunitiesWithCache(
            this.localStorageService,
            this.salesforceOrderDAO,
            cacheKey,
            this.accountId,
            term,
            () => this.searchRequestId.opportunity === requestId,
        );
    };

    public getCachedOpportunities = (): { term: string; options: IOrderFormSelectOption[] } | null =>
        getCachedOpportunitySearch(this.localStorageService, orderTemplateOpportunitySearchCacheKey(this.accountId));

    public getContractOptions(): IOrderFormSelectOption[] {
        return toIdNameOptions(this.formData?.contracts);
    }

    public getParentOrderOptions(): IOrderFormSelectOption[] {
        return (this.formData?.multiEventOrders ?? []).map(order => ({ value: order.id, label: order.orderNumber }));
    }

    public getContactOptions(): IOrderFormSelectOption[] {
        return toIdNameOptions(this.formData?.contacts);
    }

    public getEventTypeOptions(): IOrderFormSelectOption[] {
        const options = (this.formData?.eventType ?? []).map(type => ({ value: type, label: type }));
        const current = this.stringValue('eventType');
        if (current && !options.some(option => option.value === current)) {
            return [...options, { value: current, label: current }];
        }
        return options;
    }

    public getOrderSubtypeOptions(): string[] {
        const orderType = this.getValue('orderType') as string;
        return ORDER_SUBTYPE_BY_TYPE[orderType] ?? [];
    }

    private readonly lookupFields = createLookupFieldDispatch({
        corporateRelated: {
            get: () => this.corporateRelatedOption,
            set: option => { this.corporateRelatedOption = option; },
        },
        billToLegalEntity: {
            get: () => this.billToLegalEntityOption,
            set: option => { this.billToLegalEntityOption = option; },
        },
        channelAgencyReferral: {
            get: () => this.channelAgencyReferralOption,
            set: option => { this.channelAgencyReferralOption = option; },
        },
        opportunity: {
            get: () => this.opportunityOption,
            set: option => { this.opportunityOption = option; },
        },
    });

    public getAccountSearchInitialOption(fieldKey: string): IOrderFormSelectOption | null {
        return this.lookupFields.getInitialOption(fieldKey);
    }

    public getOpportunityInitialOption(): IOrderFormSelectOption | null {
        return this.lookupFields.getInitialOption('opportunity');
    }

    @mobx.action.bound
    public onAccountSelected(fieldKey: string, option: IOrderFormSelectOption | null): void {
        this.lookupFields.setOption(fieldKey, option);
    }

    @mobx.action.bound
    public setOrderType(value: FormVmValueType): void {
        applyOrderTypeChange(this.orderFieldStore(), value);
    }

    @mobx.action.bound
    public setCancellationReason(value: FormVmValueType): void {
        applyCancellationReasonChange(this.orderFieldStore(), value);
    }

    @mobx.action.bound
    public setHighProfileReasoning(value: FormVmValueType): void {
        applyHighProfileReasoningChange(this.orderFieldStore(), value);
    }

    @mobx.action.bound
    public setHighProfileOrder(value: FormVmValueType): void {
        applyHighProfileOrderChange(this.orderFieldStore(), value);
    }

    @mobx.action.bound
    public setBillingMethod(value: FormVmValueType): void {
        applyBillingMethodChange(this.orderFieldStore(), value);
    }

    @mobx.action.bound
    public setContractNumber(value: FormVmValueType): void {
        this.setConfiguredValue('contractNumber', value);
    }

    @mobx.action.bound
    public setBillingContact(value: FormVmValueType): void {
        this.setConfiguredValue('billingContact', value);
    }

    @mobx.action.bound
    public setAdditionalRecipient1(value: FormVmValueType): void {
        this.setConfiguredValue('additionalRecipient1', value);
    }

    @mobx.action.bound
    public setAdditionalRecipient2(value: FormVmValueType): void {
        this.setConfiguredValue('additionalRecipient2', value);
    }

    @mobx.action.bound
    public async submit(): Promise<boolean> {
        if (this.isSaving) {
            return false;
        }
        if (!this.isVmValid()) {
            this.setVmTouched();
            return false;
        }
        if (this.preloadBlocked) {
            alertServerError('Error', this.preloadErrorMessage || this.intlProvider.getIntl().formatMessage(ORDER_TEMPLATE_PRELOAD_ERROR_TEXT));
            return false;
        }

        const values = this.collectValues();
        const accountLabels = {
            corporateRelatedName: this.corporateRelatedOption?.label,
            billToLegalEntityName: this.billToLegalEntityOption?.label,
            opportunityName: this.opportunityOption?.label,
            channelAgencyReferralName: this.channelAgencyReferralOption?.label,
        };

        this.isSaving = true;
        try {
            const created = await this.blueprintsDAO.createOrderTemplate(
                toCreateOrderTemplateDto(this.accountId, values, accountLabels),
            );
            if (!this.isCreatedTemplate(created)) {
                OrderTemplateFormVM.handleApiException(new Error('Unable to create the order template.'));
                return false;
            }
            this.trackCreatedTemplate(values, created.blueprintId, accountLabels);
            this.apiCallSuccessCallback();
            return true;
        } catch (error) {
            OrderTemplateFormVM.handleApiException(error);
            return false;
        } finally {
            this.setSaving(false);
        }
    }

    @mobx.action
    private setFormData(data: ISalesforceOrderFormDataDTO): void {
        this.formData = data;
    }

    @mobx.action
    private setSaving(value: boolean): void {
        this.isSaving = value;
    }

    @mobx.action
    private setPreloadState(blocked: boolean, message: string): void {
        this.preloadBlocked = blocked;
        this.preloadErrorMessage = message;
    }

    private trackCreatedTemplate(
        values: Record<string, FormVmValueType>,
        templateId: string,
        names: { opportunityName?: string; channelAgencyReferralName?: string },
    ): void {
        try {
            this.trackingService.get().track(
                BLUEPRINTS_TRACKING_EVENTS.NEW_ORDER_TEMPLATE,
                toOrderTemplateTrackingEvent(values, {
                    accountId: this.accountId,
                    accountName: this.globalStorage.getAccount()?.name ?? '',
                    templateId: templateId,
                }, names),
            );
        } catch {
            // A tracking failure must not hide a template that was already saved.
        }
    }

    private isCreatedTemplate(created: IOrderTemplateDetailsDTO | null | undefined): created is IOrderTemplateDetailsDTO {
        return !!created?.blueprintId && created.type === BlueprintType.ORDER;
    }

    private orderFieldStore(): IOrderFieldValueStore {
        return {
            getValue: key => this.getValue(key),
            setValue: (key, value) => this.setConfiguredValue(key, value),
        };
    }

    private setConfiguredValue(key: string, value: FormVmValueType): void {
        if (ORDER_TEMPLATE_CONFIGURED_KEYS.has(key)) {
            this.setValue(key, value);
        }
    }

    private collectValues(): Record<string, FormVmValueType> {
        return this.getVmState().reduce((values, item) => {
            values[item.name] = item.value;
            return values;
        }, {} as Record<string, FormVmValueType>);
    }

    private stringValue(key: string): string {
        return readStringValue(this.getValue(key));
    }
}
